import React, { useState, useEffect } from 'react';
import { useNotificacoes } from '@/hooks/useNotificacoes';
import { Notificacao } from '@/types';
import { 
  Bell, 
  Search, 
  Check, 
  Trash2, 
  CheckCircle,
  MessageSquare,
  AlertCircle,
  Calendar,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';

interface Filters {
  tipo: string;
  lida: string;
}

interface Pagination {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

export default function ListaNotificacoes() {
  const { 
    notificacoes, 
    notificacoesNaoLidas, 
    totalNaoLidas,
    loading,
    error,
    carregarNotificacoes,
    marcarComoLida,
    marcarTodasComoLidas,
    deletarNotificacao,
    atualizarContador
  } = useNotificacoes();

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredNotificacoes, setFilteredNotificacoes] = useState<Notificacao[]>([]);
  const [filters, setFilters] = useState<Filters>({
    tipo: 'all',
    lida: 'all'
  });
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    itemsPerPage: 15,
    totalItems: 0,
    totalPages: 0
  });

  // Carregar notificações quando o componente montar
  useEffect(() => {
    carregarNotificacoes();
  }, [carregarNotificacoes]);

  // Filtrar notificações quando os filtros ou busca mudarem
  useEffect(() => {
    let filtered = notificacoes.filter(notificacao => {
      const matchesSearch = notificacao.mensagem.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (notificacao.ticket_assunto && notificacao.ticket_assunto.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesTipo = filters.tipo === 'all' || notificacao.tipo === filters.tipo;
      const matchesLida = filters.lida === 'all' || 
                          (filters.lida === 'lida' && notificacao.lida) ||
                          (filters.lida === 'nao_lida' && !notificacao.lida);

      return matchesSearch && matchesTipo && matchesLida;
    });

    setFilteredNotificacoes(filtered);
    
    // Atualizar paginação
    const totalItems = filtered.length;
    const totalPages = Math.ceil(totalItems / pagination.itemsPerPage);
    
    setPagination(prev => ({
      ...prev,
      currentPage: 1,
      totalItems,
      totalPages
    }));
  }, [notificacoes, searchTerm, filters, pagination.itemsPerPage]);

  // Obter itens da página atual
  const getCurrentPageItems = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return filteredNotificacoes.slice(startIndex, endIndex);
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleMarcarComoLida = async (id: number) => {
    try {
      await marcarComoLida(id);
      toast.success('Notificação marcada como lida');
    } catch (error) {
      toast.error('Erro ao marcar notificação como lida');
    }
  };

  const handleMarcarTodasComoLidas = async () => {
    try {
      await marcarTodasComoLidas();
      toast.success('Todas as notificações foram marcadas como lidas');
    } catch (error) {
      toast.error('Erro ao marcar notificações como lidas');
    }
  };

  const handleDeletarNotificacao = async (id: number) => {
    if (window.confirm('Tem certeza que deseja deletar esta notificação?')) {
      try {
        await deletarNotificacao(id);
        toast.success('Notificação deletada com sucesso');
      } catch (error) {
        toast.error('Erro ao deletar notificação');
      }
    }
  };

  const handleRefresh = async () => {
    try {
      await carregarNotificacoes();
      await atualizarContador();
      toast.success('Notificações atualizadas');
    } catch (error) {
      toast.error('Erro ao atualizar notificações');
    }
  };


  if (loading && notificacoes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 shadow-soft">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Notificações
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Gerencie suas notificações e mantenha-se informado sobre as atividades do sistema
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleRefresh}
              className="inline-flex items-center justify-center px-3 py-2 text-xs sm:text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors w-full sm:w-auto"
              title="Atualizar notificações"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Atualizar</span>
              <span className="sm:hidden">Atualizar notificações</span>
            </button>
            {totalNaoLidas > 0 && (
              <button
                onClick={handleMarcarTodasComoLidas}
                className="inline-flex items-center justify-center px-3 py-2 text-xs sm:text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors w-full sm:w-auto"
              >
                <Check className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Marcar todas como lidas</span>
                <span className="sm:hidden">Marcar todas como lidas</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
              <Bell className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{notificacoes.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400">
              <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Não Lidas</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{totalNaoLidas}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Lidas</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                {notificacoes.filter(n => n.lida).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400">
              <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Tickets</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
                {notificacoes.filter(n => n.ticket_id).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Search */}
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Buscar notificações..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 sm:py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>

          {/* Tipo Filter */}
          <select
            value={filters.tipo}
            onChange={(e) => setFilters({...filters, tipo: e.target.value})}
            className="px-3 sm:px-4 py-2.5 sm:py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="all">Todos os Tipos</option>
            <option value="ticket_criado">Ticket Criado</option>
            <option value="ticket_respondido">Ticket Respondido</option>
          </select>

          {/* Status Filter */}
          <select
            value={filters.lida}
            onChange={(e) => setFilters({...filters, lida: e.target.value})}
            className="px-3 sm:px-4 py-2.5 sm:py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-primary-400 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="all">Todos os Status</option>
            <option value="nao_lida">Não Lidas</option>
            <option value="lida">Lidas</option>
          </select>
        </div>
      </div>

      {/* Notificações List */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-soft overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Mensagem
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ticket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {getCurrentPageItems().length > 0 ? (
                getCurrentPageItems().map((notificacao) => (
                  <tr key={notificacao.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${!notificacao.lida ? 'bg-blue-50/30 dark:bg-blue-900/20' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-gray-100 max-w-xs">
                        <p className="font-medium">{notificacao.mensagem}</p>
                        {notificacao.ticket_assunto && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Assunto: {notificacao.ticket_assunto}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {notificacao.ticket_id ? (
                        <a
                          href={`/tickets/${notificacao.ticket_id}`}
                          className="text-sm text-blue-600 dark:text-blue-400 font-medium hover:underline"
                          rel="noopener noreferrer"
                          title={`Ver ticket #${notificacao.ticket_id}`}
                        >
                          # {notificacao.ticket_id}
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        notificacao.lida 
                          ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700' 
                          : 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200 border-orange-200 dark:border-orange-700'
                      }`}>
                        {notificacao.lida ? 'Lida' : 'Não lida'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                        <div>
                          <div>{new Date(notificacao.created_at).toLocaleDateString('pt-BR')}</div>
                          <div className="text-xs text-gray-400 dark:text-gray-500">{notificacao.tempo_atras}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {!notificacao.lida && (
                          <button
                            onClick={() => handleMarcarComoLida(notificacao.id)}
                            className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 p-1 rounded hover:bg-green-50 dark:hover:bg-green-900/20"
                            title="Marcar como lida"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletarNotificacao(notificacao.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Deletar notificação"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    <Bell className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                    <p className="text-lg font-medium text-gray-900 dark:text-gray-100">Nenhuma notificação encontrada</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Tente ajustar os filtros de busca</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden">
          {getCurrentPageItems().length > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {getCurrentPageItems().map((notificacao) => (
                <div key={notificacao.id} className={`p-4 ${!notificacao.lida ? 'bg-blue-50/30 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          notificacao.lida 
                            ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200' 
                            : 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200'
                        }`}>
                          {notificacao.lida ? 'Lida' : 'Não lida'}
                        </span>
                        {notificacao.ticket_id && (
                          <a
                            href={`/tickets/${notificacao.ticket_id}`}
                            className="text-xs text-blue-600 dark:text-blue-400 font-medium hover:underline"
                            rel="noopener noreferrer"
                          >
                            Ticket #{notificacao.ticket_id}
                          </a>
                        )}
                      </div>
                      
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1 line-clamp-2">
                        {notificacao.mensagem}
                      </p>
                      
                      {notificacao.ticket_assunto && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          <span className="font-medium">Assunto:</span> {notificacao.ticket_assunto}
                        </p>
                      )}
                      
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="w-3 h-3 mr-1" />
                        <span>{new Date(notificacao.created_at).toLocaleDateString('pt-BR')}</span>
                        <span className="mx-1">•</span>
                        <span>{notificacao.tempo_atras}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1 ml-3">
                      {!notificacao.lida && (
                        <button
                          onClick={() => handleMarcarComoLida(notificacao.id)}
                          className="p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-full"
                          title="Marcar como lida"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeletarNotificacao(notificacao.id)}
                        className="p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                        title="Deletar notificação"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
              <Bell className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
              <p className="text-lg font-medium text-gray-900 dark:text-gray-100">Nenhuma notificação encontrada</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Tente ajustar os filtros de busca</p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-soft">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-center sm:text-left">
              Mostrando {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} a {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} de {pagination.totalItems} resultados
            </div>
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-2">
              {/* Botão Anterior */}
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border w-full sm:w-auto ${pagination.currentPage === 1
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
              >
                ← Anterior
              </button>

              {/* Números das páginas */}
              <div className="flex items-center justify-center space-x-1 flex-wrap">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => {
                  // Mostrar apenas algumas páginas para não sobrecarregar a interface
                  if (
                    page === 1 ||
                    page === pagination.totalPages ||
                    (page >= pagination.currentPage - 1 && page <= pagination.currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border min-w-[2.5rem] sm:min-w-[2.75rem] ${page === pagination.currentPage
                          ? 'bg-blue-600 dark:bg-primary-500 text-white border-blue-600 dark:border-primary-500'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === pagination.currentPage - 2 ||
                    page === pagination.currentPage + 2
                  ) {
                    return <span key={page} className="px-1 sm:px-2 text-gray-400 dark:text-gray-500 text-xs">...</span>;
                  }
                  return null;
                })}
              </div>

              {/* Botão Próximo */}
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className={`px-3 py-2 text-xs sm:text-sm font-medium rounded-lg border w-full sm:w-auto ${pagination.currentPage === pagination.totalPages
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-600 cursor-not-allowed'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
              >
                Próximo →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barra de informações */}
      {filteredNotificacoes.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div>
            Mostrando {filteredNotificacoes.length} de {notificacoes.length} notificação(ões)
          </div>
          {error && (
            <div className="text-red-600 dark:text-red-400">
              Erro: {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
